import { useSelector } from 'react-redux';
import { hasModuleAccess, hasWriteAccess, can as canCapability } from '../utils/permissions';

/**
 * The single client-side permission hook. All UI gating goes through it.
 *
 * Usage:
 *   const { can, canAccess, canWrite, isAdmin } = usePermissions();
 *   if (can('labResults:write')) { ... }   // granular capability
 *   if (canAccess('labOrders')) { ... }      // module read (sidebar/tab)
 *   if (canWrite('labOrders')) { ... }       // module write
 *
 * Capabilities + isSuperAdmin arrive from the server on myPermissions
 * (GET /api/permissions/:role) and are kept fresh by the layout on navigation.
 */
export function usePermissions() {
    const { userData } = useSelector((state) => state.auth);
    const { myPermissions } = useSelector((state) => state.permissions);

    const role = userData?.role;
    const isAdmin = !!myPermissions?.isSuperAdmin || role === 'admin';

    return {
        can: (capability) => isAdmin || canCapability(myPermissions, capability),
        canAccess: (moduleKey) => isAdmin || hasModuleAccess(myPermissions, role, moduleKey),
        canWrite: (moduleKey) => isAdmin || hasWriteAccess(myPermissions, role, moduleKey),
        isAdmin,
        role,
        permissionsLoaded: isAdmin || !!myPermissions,
    };
}

export default usePermissions;
